import React, { useEffect, useState, useRef, useCallback } from 'react'
import {
  Button,
  DialogOverlayActions,
  Paragraph,
  Separator,
  MenuOverlay,
  ListItem,
  useMenuOverlay,
  Row,
} from '@fs/zion-ui'
import ZionForm, { useZionForm, FormTextField } from '@fs/zion-form'
import { useTranslation } from 'react-i18next'
import { useChineseSurnameInfo } from '../../providers/ChineseSurnameProvider'
import { useMemoryBook } from '../../providers/MemoryBookProvider'
import { fetchSurnameData } from '../../services/SurnameAPI/fetchSurnameData'
import calculatePageParagraphs from '../../helpers/calculatePageParagraphs/calculatePageParagraphs'
import { processHtmlDefinition } from '../../helpers/processHtmlDefinition/processHtmlDefinition'
import useDeviceSize from '../../hooks/useDeviceSize/useDeviceSize'

const DEBOUNCE_MS = 400

function useDebounce(callback, delay) {
  const timerRef = useRef(null)

  useEffect(() => {
    return () => timerRef.current && clearTimeout(timerRef.current)
  }, [])

  return useCallback(
    (...args) => {
      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => callback(...args), delay)
    },
    [callback, delay]
  )
}

const FormActions = ({ overlay, isValid, isDirty, isSubmitDisabled, isGlobalSurnamePage }) => {
  const [t] = useTranslation()

  return (
    <DialogOverlayActions>
      <Button onClick={overlay.close}>{t('common-ui:cancel.action', 'Cancel')}</Button>
      <Button
        type="submit"
        emphasis="high"
        disabled={isSubmitDisabled || !isValid || !isDirty}
        linkName={
          isGlobalSurnamePage ? 'Button:Selected Global Surname:Submit' : 'Button:Selected Chinese Surname:Submit'
        }
      >
        {t('common-ui:submit.action', 'Submit')}
      </Button>
    </DialogOverlayActions>
  )
}

const FormContent = ({ focusRef, onChange, surnameSuggestions, menuOverlay, onSurnameSelect, isGlobalSurnamePage }) => {
  const [t] = useTranslation()

  const renderTextField = () => {
    if (isGlobalSurnamePage) {
      return (
        <FormTextField
          ref={focusRef}
          onChange={onChange}
          name="surname"
          type="text"
          autoComplete="false"
          data-testid="global-surname-text-field"
          growX
        />
      )
    }

    return (
      <FormTextField
        ref={focusRef}
        placeholder="Li"
        onChange={onChange}
        name="surname"
        type="search"
        autoComplete="false"
        data-testid="surname-text-field"
        {...menuOverlay.menuButtonProps}
        growX
      />
    )
  }

  return (
    <Row>
      <Paragraph noShrinkX>{t('LayoutPickerOverlay.surnameSelection.paragraph', 'Enter a surname:')}</Paragraph>
      {renderTextField()}
      {!isGlobalSurnamePage && (
        <MenuOverlay
          {...menuOverlay}
          aria-label={t('LayoutPickerOverlay.surnameSelection.suggestions', 'Surname Suggestions')}
          inline={false}
        >
          {surnameSuggestions.length > 0 &&
            surnameSuggestions.map((surname) => (
              <ListItem key={surname.id} primaryText={surname.surname} onClick={() => onSurnameSelect(surname)} />
            ))}
        </MenuOverlay>
      )}
    </Row>
  )
}

export default function SurnameSelectionFormView({ overlay, pageNumber, focusRef, isGlobalSurnamePage = false }) {
  const [t] = useTranslation()
  const { surnameSuggestions, searchSurnames, fetchNextSurnameColor } = useChineseSurnameInfo()
  const { updatePageLayout, addBookPages } = useMemoryBook() || {}
  const menuOverlay = useMenuOverlay()
  const { isTabletOrSmaller } = useDeviceSize()
  const [selectedSurnameId, setSelectedSurnameId] = useState(null)
  const [searchError, setSearchError] = useState(false)
  const [previousValue, setPreviousValue] = useState('')

  const validate = (values) => {
    const errors = {}

    if (isGlobalSurnamePage) {
      // Validation for global surnames
      const surnameValue = values.surname
      if (surnameValue) {
        // Allow letters (including accented characters), spaces, hyphens, apostrophes, and periods
        const validSurnamePattern = /^[a-zA-ZÀ-ÿĀ-žА-я\u4e00-\u9fff\s\-'.]+$/

        if (!validSurnamePattern.test(surnameValue)) {
          errors.surname = {
            message: t(
              'LayoutPickerOverlay.surnameSelection.invalidChars',
              'Please enter only letters, spaces, hyphens, and apostrophes. Numbers and special symbols are not allowed.'
            ),
          }
        }
      }
    } else if (searchError) {
      // Original validation for Chinese surnames
      errors.surname = {
        message: t(
          'LayoutPickerOverlay.surnameSelection.error',
          'Surname not found. Please enter Hanyu Pinyin, Chinese characters, or try a different surname.'
        ),
      }
    }

    return { values, errors }
  }

  const methods = useZionForm({
    mode: 'all',
    resolver: validate,
    defaultValues: {
      surname: '',
    },
  })

  const {
    reset,
    formState: { isValid, isDirty },
    getValues,
    setValue,
    trigger,
  } = methods

  const debouncedSearch = useDebounce(async (value) => {
    if (!value.trim()) return

    const result = await searchSurnames(value)
    setSearchError(result.noResults)

    if (result.noResults) {
      trigger('surname')
    }

    if (focusRef.current) {
      menuOverlay.open({ target: focusRef.current })
    }
  }, DEBOUNCE_MS)

  useEffect(() => {
    if (!overlay.isOpen && !overlay.transitioning) {
      reset()
      setSelectedSurnameId(null)
      setSearchError(false)
      menuOverlay.close()
      setPreviousValue('')
    }
  }, [reset, overlay, menuOverlay])

  const onSubmit = async () => {
    const values = getValues()
    const surnameId = isGlobalSurnamePage ? values.surname : selectedSurnameId
    if (surnameId) {
      if (isGlobalSurnamePage) {
        // Handle global surname page logic
        try {
          const surnameData = await fetchSurnameData(surnameId)

          if (!surnameData) {
            console.error(`No surname data found for ${surnameId}`)
            return
          }

          // Process paragraphs similar to GlobalSurnamePageWrapper
          const arrayParagraphs = processHtmlDefinition(surnameData?.htmlDefinition)

          // Calculate paragraphs for first page
          const { remainingParagraphs } = calculatePageParagraphs(arrayParagraphs)

          // If we have remaining paragraphs, create a second page
          if (remainingParagraphs.length > 0) {
            // Add a new page after the current one
            await addBookPages(1, pageNumber, isTabletOrSmaller)

            // Update the second page with remaining content
            await updatePageLayout(pageNumber + 1, 17, {
              surnameId,
              surnamePage: '2',
            })
          }

          // Update the first page with initial content
          await updatePageLayout(pageNumber, 17, {
            surnameId,
            surnamePage: '1',
          })
        } catch (error) {
          console.error('Error in global surname submission:', error)
          return
        }
      } else {
        // Handle Chinese surname page logic
        const color = fetchNextSurnameColor()
        updatePageLayout(pageNumber, 2, { surnameId, color })
      }
    }

    overlay.close()
  }

  const onChange = () => {
    const values = getValues()
    const newValue = values.surname
    const isDeleting = newValue.length < previousValue.length

    setSearchError(false)
    setSelectedSurnameId(null)
    setPreviousValue(newValue)

    if (isGlobalSurnamePage) {
      // Trigger validation for global surnames
      trigger('surname')
    } else {
      if (!newValue.trim()) {
        menuOverlay.close()
        searchSurnames('')
        return
      }

      if (!isDeleting) {
        debouncedSearch(newValue)
      }
    }
  }

  const onSurnameSelect = (surname) => {
    setValue('surname', surname.surname)
    setSelectedSurnameId(surname.id)
    setSearchError(false)
    menuOverlay.close()
  }

  return (
    <ZionForm methods={methods} onSubmit={onSubmit}>
      <FormContent
        focusRef={focusRef}
        onChange={onChange}
        surnameSuggestions={surnameSuggestions}
        menuOverlay={menuOverlay}
        onSurnameSelect={onSurnameSelect}
        isGlobalSurnamePage={isGlobalSurnamePage}
      />
      <Separator />
      <FormActions
        overlay={overlay}
        isValid={isValid}
        isDirty={isDirty}
        isGlobalSurnamePage={isGlobalSurnamePage}
        isSubmitDisabled={
          (!isGlobalSurnamePage && !selectedSurnameId) || (isGlobalSurnamePage && (!isValid || !isDirty))
        }
      />
    </ZionForm>
  )
}
